import { useEffect, useRef } from 'react'

import { format } from 'date-fns'

import { toast } from '@buffer-mono/popcorn'
import { BufferTrackerReact as BufferTracker } from '@buffer-mono/tracking-plan'

type CalendarAnalytics = {
  onCalendarChangeTrack: (previousView: string, changeType: string) => void
  onWeekClick: (isWeekly: boolean) => void
  onMonthClick: (isWeekly: boolean) => void
  onDropPostSuccess: (args: { dueAt: string; isDraft: boolean }) => void
  onDropPostError: (error?: { message: string } | Error | null) => void
}

type CalendarAnalyticsProps = {
  granularity: 'week' | 'month' | 'three-day'
  channelsCount: number
  organizationId: string
  trackOpened?: boolean
}

export const useCalendarAnalytics = (
  props: CalendarAnalyticsProps,
): CalendarAnalytics => {
  const { granularity, channelsCount, organizationId, trackOpened } = props
  const trackedOrganizationId = useRef<string | undefined>(undefined)

  // track the opening of the calendar
  // TODO: create a more sophisticated tracking for filters and tab switches
  // See: https://linear.app/buffer/issue/CONT-161
  useEffect(() => {
    if (
      trackOpened === false ||
      trackedOrganizationId.current === organizationId
    ) {
      return
    }

    const view = granularity === 'three-day' ? 'day' : granularity
    BufferTracker.calendarOpened({
      organizationId,
      clientName: 'publishWeb',
      product: 'publish',
      calendarVersion: 'new',
      view,
      channelCount: channelsCount,
      source: 'sidebar',
    })
    trackedOrganizationId.current = organizationId
  }, [organizationId, granularity, channelsCount, trackOpened])

  return {
    onCalendarChangeTrack: (previousView: string, changeType: string): void => {
      BufferTracker.calendarDatesChanged({
        organizationId,
        clientName: 'publishWeb',
        product: 'publish',
        calendarVersion: 'new',
        view: previousView,
        dateChangeType: changeType,
        source: 'sidebar',
      })
    },
    onWeekClick: (isWeekly: boolean): void => {
      BufferTracker.calendarViewSwitched({
        organizationId,
        clientName: 'publishWeb',
        product: 'publish',
        calendarVersion: 'new',
        view: 'week',
        channelCount: channelsCount,
        previousView: isWeekly ? 'week' : 'month',
        source: 'sidebar',
      })
    },
    onMonthClick: (isWeekly: boolean): void => {
      BufferTracker.calendarViewSwitched({
        organizationId,
        clientName: 'publishWeb',
        product: 'publish',
        calendarVersion: 'new',
        view: 'month',
        channelCount: channelsCount,
        source: 'sidebar',
        previousView: isWeekly ? 'week' : 'month',
      })
    },
    onDropPostSuccess: ({
      dueAt,
      isDraft,
    }: {
      dueAt: string
      isDraft: boolean
    }): void => {
      const newDate = new Date(dueAt)
      const formattedDate = format(newDate, "eee, MMMM d 'at' HH:mm")
      toast.success(
        `Great! We’ve rescheduled your ${
          isDraft ? 'draft' : 'post'
        } for ${formattedDate}`,
      )
    },
    onDropPostError: (error?: { message: string } | Error | null): void => {
      const maybeErrorMessage =
        error && 'message' in error ? error.message : undefined
      const fallbackErrorMessage =
        'Whoops, we had some problems updating the date for that post!'
      toast.error(maybeErrorMessage || fallbackErrorMessage)
    },
  }
}
